import { useDemoGuard, useInspections } from '@hooks';
import { useStore } from '@stores';
import { observer } from 'mobx-react';
import React, { useCallback, useMemo, useState } from 'react';
import Presentation from './OrderInspectionTable.presentation';

interface IProps {
  orderId?: string;
  vrm?: string;
  customerId?: string;
  customerEmail?: string;
}

export const OrderInspectionTable: React.FC<IProps> = observer(
  ({ orderId, vrm, customerId, customerEmail: customerEmailProp }) => {
    const { inspectionStore, orderStore } = useStore();
    const customerEmail = customerEmailProp ?? orderStore.order?.customer?.email;
    const [open, setOpen] = useState(false);
    const [inspectionId, setInspectionId] = useState<string | null>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [vhcEmailModalOpen, setVhcEmailModalOpen] = useState(false);
    const [vhcEmailInspectionId, setVhcEmailInspectionId] = useState<string | null>(null);
    const [vhcEmail, setVhcEmail] = useState('');
    const [vhcEmailError, setVhcEmailError] = useState<string | null>(null);
    const [vhcEmailSending, setVhcEmailSending] = useState(false);

    const inspectionsHook = useInspections({ orderId, vrm, customerId });

    // What the picker greys out: the categories already on the job, straight from the list.
    const addedCategoryIds = inspectionsHook.dataSource.map((inspection) => inspection.category.id);

    const { refresh } = inspectionsHook;

    const handleAddInspection = useCallback(
      async (categoryId: number) => {
        if (!orderId) return;

        await inspectionStore.createOrderInspection({ orderId, categoryId });

        // The row the API hands back is the bare entity, without the counts the list draws its
        // progress from. Re-read so a checklist just added shows 0 of however many rather than
        // nothing at all.
        refresh();
      },
      [inspectionStore, orderId, refresh]
    );

    const handleDeleteInspection = useCallback(
      async (categoryId: number) => {
        orderId && (await inspectionStore.deleteOrderInspection({ categoryId, orderId }));
      },
      [inspectionStore, orderId]
    );

    const handleOpen = (inspectionId: string) => {
      setOpen(true);
      setInspectionId(inspectionId);
    };

    const handleClose = () => {
      setOpen(false);
      setInspectionId(null);

      // The row shows how far along the checklist is, and the editing just moved it.
      inspectionsHook.refresh();
    };

    const handlePublish = useCallback(
      async (id: string) => inspectionStore.publish({ id }),
      [inspectionStore]
    );

    const validateVhcEmail = (val: string) => {
      if (!val) return null;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(val)) return 'Invalid email address';
      return null;
    };

    const handleVhcEmailChange = (val: string) => {
      setVhcEmail(val);
      setVhcEmailError(validateVhcEmail(val));
    };

    const { guard } = useDemoGuard();

    const handleVhcEmailOpen = useMemo(
      () =>
        guard((id: string) => {
          setVhcEmailInspectionId(id);
          setVhcEmail(customerEmail?.trim() || '');
          setVhcEmailError(null);
          setVhcEmailModalOpen(true);
        }),
      [customerEmail, guard]
    );

    const handleVhcEmailSend = useCallback(async () => {
      if (!vhcEmailInspectionId) return;

      const error = validateVhcEmail(vhcEmail);
      setVhcEmailError(error);
      if (error) return;

      setVhcEmailSending(true);
      try {
        await inspectionStore.sendInspectionEmail(vhcEmailInspectionId, vhcEmail || customerEmail);
        setVhcEmailModalOpen(false);
      } catch {
        // error handled by store
      } finally {
        setVhcEmailSending(false);
      }
    }, [inspectionStore, vhcEmailInspectionId, vhcEmail]);

    const handleVhcEmailCancel = useCallback(() => {
      setVhcEmailModalOpen(false);
      setVhcEmailInspectionId(null);
      setVhcEmail('');
      setVhcEmailError(null);
    }, []);

    return (
      <Presentation
        open={open}
        readOnly={!orderId}
        inspectionId={inspectionId}
        inspectionsHook={inspectionsHook}
        pickerOpen={pickerOpen}
        addedCategoryIds={addedCategoryIds}
        vhcEmailModalOpen={vhcEmailModalOpen}
        vhcEmail={vhcEmail}
        vhcEmailError={vhcEmailError}
        vhcEmailSending={vhcEmailSending}
        handleOpen={handleOpen}
        handleClose={handleClose}
        handlePublish={handlePublish}
        handleVhcEmailOpen={handleVhcEmailOpen}
        handleAddInspection={handleAddInspection}
        handleDeleteInspection={handleDeleteInspection}
        setPickerOpen={setPickerOpen}
        handleVhcEmailChange={handleVhcEmailChange}
        handleVhcEmailSend={handleVhcEmailSend}
        handleVhcEmailCancel={handleVhcEmailCancel}
      />
    );
  }
);
